import React, { useState } from 'react';
import { Code, Eye, Bold, Italic, List, ListOrdered, Link as LinkIcon, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';

/**
 * RichTextEditor - A simple rich text editor with Visual and HTML modes
 * Supports basic formatting without external dependencies
 */
const RichTextEditor = ({ value = '', onChange, placeholder = 'Enter content...', rows = 8 }) => {
  const [mode, setMode] = useState('visual'); // 'visual' or 'html'
  const [internalValue, setInternalValue] = useState(value);
  const editorRef = React.useRef(null);

  React.useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const handleChange = (newValue) => {
    setInternalValue(newValue);
    onChange(newValue);
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    // Get the HTML content after the command
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      handleChange(html);
    }
  };

  const insertHTML = (html) => {
    if (editorRef.current) {
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        const div = document.createElement('div');
        div.innerHTML = html;
        const frag = document.createDocumentFragment();
        let node;
        while ((node = div.firstChild)) {
          frag.appendChild(node);
        }
        range.insertNode(frag);
        handleChange(editorRef.current.innerHTML);
      }
    }
  };

  const handleContentChange = () => {
    if (editorRef.current && mode === 'visual') {
      handleChange(editorRef.current.innerHTML);
    }
  };

  const handleTextareaChange = (e) => {
    handleChange(e.target.value);
  };

  const formatButtons = [
    { icon: Bold, command: 'bold', title: 'Bold (Ctrl+B)' },
    { icon: Italic, command: 'italic', title: 'Italic (Ctrl+I)' },
    { icon: List, command: 'insertUnorderedList', title: 'Bullet List' },
    { icon: ListOrdered, command: 'insertOrderedList', title: 'Numbered List' },
    { icon: AlignLeft, command: 'justifyLeft', title: 'Align Left' },
    { icon: AlignCenter, command: 'justifyCenter', title: 'Align Center' },
    { icon: AlignRight, command: 'justifyRight', title: 'Align Right' },
  ];

  const headingOptions = [
    { label: 'Paragraph', value: 'p', command: 'formatBlock' },
    { label: 'Heading 1', value: 'h1', command: 'formatBlock' },
    { label: 'Heading 2', value: 'h2', command: 'formatBlock' },
    { label: 'Heading 3', value: 'h3', command: 'formatBlock' },
  ];

  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="bg-gray-50 border-b border-gray-300 p-2 flex items-center gap-2 flex-wrap">
        {/* Mode Toggle */}
        <div className="flex border border-gray-300 rounded overflow-hidden">
          <button
            type="button"
            onClick={() => setMode('visual')}
            className={`px-3 py-1 text-sm font-medium flex items-center gap-1 ${
              mode === 'visual'
                ? 'bg-theme-blue text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
            title="Visual Editor"
          >
            <Eye className="h-4 w-4" />
            Visual
          </button>
          <button
            type="button"
            onClick={() => setMode('html')}
            className={`px-3 py-1 text-sm font-medium flex items-center gap-1 ${
              mode === 'html'
                ? 'bg-theme-blue text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
            title="HTML Code"
          >
            <Code className="h-4 w-4" />
            HTML
          </button>
        </div>

        <div className="w-px h-6 bg-gray-300"></div>

        {/* Formatting buttons - only show in visual mode */}
        {mode === 'visual' && (
          <>
            {/* Heading selector */}
            <select
              onChange={(e) => {
                if (e.target.value) {
                  execCommand('formatBlock', e.target.value);
                  e.target.value = '';
                }
              }}
              className="text-sm px-2 py-1 border border-gray-300 rounded bg-white hover:bg-gray-50"
            >
              <option value="">Format</option>
              {headingOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>

            <div className="w-px h-6 bg-gray-300"></div>

            {/* Format buttons */}
            {formatButtons.map((btn, index) => (
              <button
                key={index}
                type="button"
                onClick={() => execCommand(btn.command)}
                className="p-1.5 text-gray-600 hover:bg-gray-200 rounded transition-colors"
                title={btn.title}
              >
                <btn.icon className="h-4 w-4" />
              </button>
            ))}

            <div className="w-px h-6 bg-gray-300"></div>

            {/* Link button */}
            <button
              type="button"
              onClick={insertLink}
              className="p-1.5 text-gray-600 hover:bg-gray-200 rounded transition-colors"
              title="Insert Link"
            >
              <LinkIcon className="h-4 w-4" />
            </button>
          </>
        )}
      </div>

      {/* Editor Area */}
      {mode === 'visual' ? (
        <div
          ref={editorRef}
          contentEditable
          onInput={handleContentChange}
          onBlur={handleContentChange}
          className="w-full px-3 py-2 focus:outline-none focus:ring-2 focus:ring-theme-blue min-h-[200px] max-h-[400px] overflow-y-auto"
          style={{ minHeight: `${rows * 1.5}rem` }}
          dangerouslySetInnerHTML={{ __html: internalValue }}
        />
      ) : (
        <textarea
          value={internalValue}
          onChange={handleTextareaChange}
          rows={rows}
          className="w-full px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-theme-blue"
          placeholder={placeholder}
        />
      )}

      {/* Help text */}
      <div className="bg-gray-50 border-t border-gray-300 px-3 py-2">
        <p className="text-xs text-gray-500">
          {mode === 'visual' 
            ? 'Use the toolbar to format your content. Switch to HTML mode to edit the raw HTML.'
            : 'Edit HTML directly. Switch to Visual mode to use the formatting toolbar.'
          }
        </p>
      </div>
    </div>
  );
};

export default RichTextEditor;
